import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import mobileMockup from '../../assets/home/mobile_mockup.png';
import iphoneHandMockup from '../../assets/home/mockup_iphone_hand.jpg';
import { AppsButtonContainer } from '../../component/button/appsButtonContainer';
import { useTranslation } from '../../services/LanguageContext';
import './home.scss';

// HeroSection Component
const HeroSection = () => {
  const { lang } = useParams();
  const { t, isLoading } = useTranslation();

  if (isLoading) {
    return (
      <div className="hero-section">
        <div className="hero-overlay d-flex flex-column align-items-center justify-content-center">
          <h2 className='text-white'>Laden...</h2>
          <span className='hero-text-margin'> </span>
          <AppsButtonContainer />
        </div>
      </div>
    );
  }

  return (
    <div className="hero-section">
      <div className="hero-overlay d-flex flex-column align-items-center justify-content-center">
        <h2 className='text-white'>{t('home_hero_title', 'Finde dein perfektes Match')}</h2>
        <span className='hero-text-margin'> </span>
        <div className="d-flex gap-3 mb-4">
          <a 
            href="https://find-together-app.sslbeta.de/#/register" 
            className="btn hero-button-primary btn-lg"
          >
            {t('nav_get_started', 'Kostenlos starten')}
          </a>
          <a 
            href="https://find-together-app.sslbeta.de/#/login" 
            className="btn hero-button-light btn-lg"
          >
            {t('login', 'Anmelden')}
          </a>
        </div>
        <AppsButtonContainer />
      </div>
    </div>
  );
};

// Features Component
const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      titleKey: 'matching_title',
      descriptionKey: 'matching_description',
      icon: 'bi-heart',
      fallbackTitle: 'Einfache Vermittlung',
          fallbackDescription: 'Finde deinen perfekten Partner mit unserem intelligenten Matching-Algorithmus.'
    },
    {
      titleKey: 'verified_title', 
      descriptionKey: 'verified_description',
      icon: 'bi-shield-check',
      fallbackTitle: 'Verifizierte Profile',
      fallbackDescription: 'Verbinde dich mit echten und verifizierten Mitgliedern.'
    },
    {
      titleKey: 'free_title',
      descriptionKey: 'free_description', 
      icon: 'bi-gift',
      fallbackTitle: 'Kostenlos',
      fallbackDescription: 'Genieße alle Funktionen kostenlos!'
    }
  ];

  return (
    <div className="container features section-margin">
      <h2 className="text-center my-4">{t('features_title', 'Funktionen')}</h2>
      <Row>
        {features.map((feature, index) => (
          <Col key={index} sm={12} md={4} className="feature-col">
            <div className="home-card feature">
              <div className="feature-img-container">
                <i className={`bi ${feature.icon} feature-icon`}></i>
              </div>
              <h3 className="feature-title">
                {t(feature.titleKey, feature.fallbackTitle)}
              </h3>
              <p>{t(feature.descriptionKey, feature.fallbackDescription)}</p>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// AppHighlight Component
const AppHighlightSection = () => {
  const { t } = useTranslation();

  return (
    <div className="app-highlight featurette text-center">
      <div className='container section-margin'>
        <div className="row align-items-center">
          <div className="col-md-5">
            <img src={mobileMockup} alt="Mobile App" className="featurette-image img-fluid mx-auto" />
          </div>
          <div className="col-md-7 p-5">
            <h2 className="featurette-heading">
              {t('app_highlight_title', 'Entdecke die Zukunft des Datings.')}
            </h2>
            <p className="lead">
                          {t('app_highlight_description', 'Unsere App bringt die Welt des Datings direkt auf dein Smartphone. Verbinde dich mit echten, verifizierten Profilen, chatte sicher und erlebe intelligentes Matching wie nie zuvor.')}
            </p>
            <div className="download-buttons">
              <AppsButtonContainer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PrototypeSection Component
const PrototypeSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      titleKey: 'customizable_title',
      descriptionKey: 'customizable_description',
      icon: 'bi-sliders',
      fallbackTitle: 'Anpassbare Profileinstellungen',
      fallbackDescription: 'Passe dein Profil und Suchkriterien nach Belieben an.'
    },
    {
      titleKey: 'social_login_title',
      descriptionKey: 'social_login_description',
      icon: 'bi-shield-lock',
      fallbackTitle: 'Social Login-Optionen',
      fallbackDescription: 'Integriere beliebige Social-Media-Logins.'
    },
    {
      titleKey: 'invitations_title',
      descriptionKey: 'invitations_description',
      icon: 'bi-person-check',
      fallbackTitle: 'Eingeschränkte Einladungen',
      fallbackDescription: 'Sende exklusive Einladungen an ausgewählte Benutzer.'
    },
    {
      titleKey: 'chat_title',
      descriptionKey: 'chat_description',
      icon: 'bi-chat-dots',
      fallbackTitle: 'Chat und Bildfreigabe',
      fallbackDescription: 'Chatte sicher und teile Bilder in Echtzeit.'
    },
    {
      titleKey: 'search_title',
      descriptionKey: 'search_description',
      icon: 'bi-search',
      fallbackTitle: 'Suche nach verschiedenen Kriterien',
      fallbackDescription: 'Verwende erweiterte Filter, um den perfekten Partner zu finden.'
    },
    {
      titleKey: 'friends_title',
      descriptionKey: 'friends_description',
      icon: 'bi-person-plus',
      fallbackTitle: 'Freunde hinzufügen',
      fallbackDescription: 'Füge neue Freunde hinzu und erweitere dein Netzwerk.'
    }
  ];

  return (
    <section id="alt-features" className="alt-features section section-margin">
      <div className='container'>
        <div className="row">
          <div className="col-xl-7 d-flex order-2 order-xl-1" data-aos="fade-up" data-aos-delay="200">
            <div className="row align-self-center gy-5 p-5">
              {features.map((feature, index) => (
                <div className="col-md-6 icon-box" key={index}>
                  <i className={`bi ${feature.icon}`}></i>
                  <div>
                    <h4>{t(feature.titleKey, feature.fallbackTitle)}</h4>
                    <p>{t(feature.descriptionKey, feature.fallbackDescription)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-xl-5 d-flex align-items-center order-1 order-xl-2" data-aos="fade-up" data-aos-delay="100">
            <img src={iphoneHandMockup} className="img-fluid prototype-img " alt="App Mockup" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Component
const Home = () => {
  return (
    <>
      <HeroSection />
      <div className="container">
        <Features />
      </div>
      <AppHighlightSection />
      <PrototypeSection />
    </>
  );
};

export default Home;
